/**
 * Declarative lesson descriptions. Lesson factories return one of these
 * configs; the React `LessonRenderer` maps a config to a view component.
 */

/** Delay before advancing after a correct answer/run so the user sees the result. */
export const FINISH_DELAY_MS = 1200;

/**
 * A single unit test. The plain-string form is a goal world name (the start
 * world is the map key). The object form additionally supports pre-run inputs
 * and exact expected console output, for input/output lessons.
 */
export type UnitTestSpec =
  | string
  | {
      goal?: string;
      inputs?: string[];
      output?: string;
    };

export type UnitTests = Record<string, UnitTestSpec>;

export interface IdeConfig {
  type: "ide-demo" | "ide-lesson";
  world: string;
  starterCode: string;
  unitTests?: UnitTests;
  /** Show the input/output panels for lessons that read input or print output. */
  io?: boolean;
}

export interface ButtonLessonConfig {
  type: "button";
  world: string;
  /**
   * Ordered command tokens the goal world animates through. Tokens are stored
   * in canonical camelCase (`turnLeft`, `putBeeper`); the canvas normalizes
   * snake_case aliases so lessons can also be authored Python-style.
   */
  solution: string[];
  buttons: Record<string, boolean>;
}

/**
 * A picture in a picture lesson: either a static image URL or a Karel world
 * name. Worlds are rendered through the shared canvas renderer so their art
 * can never drift from the world model.
 */
export type PictureSource = string | { world: string };

export interface PictureConfig {
  type: "picture-demo" | "picture";
  images: PictureSource[];
  labels: string[];
}

export type LessonConfig = IdeConfig | ButtonLessonConfig | PictureConfig;
